import React from 'react';
import {
    CheckOutlined,
    DownOutlined,
    LoginOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Dropdown, Layout, Menu, Space, Tooltip } from 'antd';
const { Header } = Layout;

import { defineMessages } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import flagDe from '@assets/images/flags/de.svg';
import flagEn from '@assets/images/flags/en.svg';
import flagVi from '@assets/images/flags/vi.svg';
import { AppConstants } from '@constants';
import apiConfig from '@constants/apiConfig';
import useAuth from '@hooks/useAuth';
import useFetch from '@hooks/useFetch';
import useLocale from '@hooks/useLocale';
import useTranslate from '@hooks/useTranslate';
import { removeCacheToken } from '@services/userService';
import { accountActions, appActions } from '@store/actions';

import SessionTimeout from './SessionTimeout';

import styles from './AppHeader.module.scss';

const message = defineMessages({
    profile: 'Profile',
    logout: 'Logout',
    language: {
        id: 'header.language',
        defaultMessage: 'Language',
    },
});

const languageOptions = [
    { value: 'en', label: 'English', flag: flagEn },
    { value: 'de', label: 'Deutsch', flag: flagDe },
    { value: 'vi', label: 'Tiếng Việt', flag: flagVi },
];

const AppHeader = ({ collapsed, onCollapse }) => {
    const { locale } = useLocale();
    const { profile } = useAuth();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { execute: executeLogout } = useFetch(apiConfig.account.logout);
    const translate = useTranslate();
    const selectedLanguage = languageOptions.find((item) => item.value === locale) || languageOptions[0];

    const handleChangeLocale = (nextLocale) => {
        dispatch(appActions.changeLanguage(nextLocale));
    };

    const onLogout = () => {
        executeLogout();
        removeCacheToken();
        dispatch(accountActions.logout());
    };

    return (
        <Header className={styles.appHeader} style={{ padding: 0, background: 'white' }}>
            <span className={styles.iconCollapse} onClick={onCollapse}>
                {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </span>
            <div className={styles.headerActions}>
                <SessionTimeout />
                <Dropdown
                    trigger={[ 'click' ]}
                    placement="bottomRight"
                    menu={{
                        selectedKeys: [ locale ],
                        items: languageOptions.map((item) => ({
                            key: item.value,
                            label: item.label,
                            icon: (
                                <span className={styles.languageOptionIcon}>
                                    <img src={item.flag} alt="" />
                                    {locale === item.value ? <CheckOutlined /> : <span className={styles.checkSpacer} />}
                                </span>
                            ),
                            onClick: () => handleChangeLocale(item.value),
                        })),
                    }}
                >
                    <Tooltip title={translate.formatMessage(message.language)}>
                        <Button
                            className={styles.languageButton}
                            type="text"
                            icon={<img src={selectedLanguage.flag} alt={selectedLanguage.label} />}
                            aria-label={translate.formatMessage(message.language)}
                        />
                    </Tooltip>
                </Dropdown>
                <Menu
                    mode="horizontal"
                    className={styles.rightMenu}
                    selectedKeys={[]}
                    items={[
                        {
                            key: 'menu',
                            label: (
                                <Space>
                                    <Avatar
                                        icon={<UserOutlined />}
                                        src={
                                            profile?.isSuperAdmin
                                                ? profile.avatar &&
                                                  `${AppConstants.contentRootUrl}${profile.avatar}`
                                                : profile.avatarPath &&
                                                  `${AppConstants.contentRootUrl}${profile.avatarPath}`
                                        }
                                    />
                                    {profile?.fullName}
                                    <DownOutlined />
                                </Space>
                            ),
                            children: [
                                {
                                    label: translate.formatMessage(message.profile),
                                    icon: <UserOutlined />,
                                    key: 'profile',
                                    onClick: () => navigate('/profile'),
                                },
                                {
                                    label: translate.formatMessage(message.logout),
                                    icon: <LoginOutlined />,
                                    key: 'logout',
                                    onClick: onLogout,
                                },
                            ],
                        },
                    ]}
                />
            </div>
        </Header>
    );
};

export default AppHeader;
